import { useCallback } from 'react';

import { IServerOrder } from '@rbi-ctg/menu';
import {
  IDeliveryWaypointInput,
  Maybe,
  useValidateCommitOrderMutation,
} from 'generated/rbi-graphql';
import useErrorModal from 'hooks/use-error-modal';
import { AuthInterface } from 'state/auth';
import { buildCommitDeliveryInput } from 'utils/cart/build-commit-delivery-input';

import { useHandleCommitError } from '../hooks/use-handle-commit-error';

import { getOrderFailureMessageKey } from './get-error-dialog-options';

export interface UseValidateCommitOrder {
  onCoolingPeriodConfirm: () => void;
}

export const useValidateCommitOrder = ({ onCoolingPeriodConfirm }: UseValidateCommitOrder) => {
  const [ErrorDialog, openErrorDialog, dismissErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Order Commit Error',
  });

  const { handleCommitError } = useHandleCommitError({
    errorMsgId: getOrderFailureMessageKey('', undefined),
    openErrorDialog,
    dismissErrorDialog,
    onCoolingPeriodConfirm,
    commitError: undefined,
    confirmationTextId: 'continue',
  });

  const [fireMutation] = useValidateCommitOrderMutation();

  const validateCommitOrder = useCallback(
    async (
      serverOrder: IServerOrder,
      order: any,
      auth: AuthInterface,
      skipCoolingPeriod: boolean
    ) => {
      const delivery = buildCommitDeliveryInput(order, auth.user);
      try {
        await fireMutation({
          variables: {
            input: {
              rbiOrderId: serverOrder.rbiOrderId,
              delivery: delivery
                ? {
                    dropoff: delivery.dropoff as Maybe<IDeliveryWaypointInput>,
                    instructions: delivery.instructions,
                    pushNotification: delivery.pushNotification,
                    tipCents: delivery.tipCents,
                  }
                : undefined,
              fireOrderIn: order.fireOrderIn,
              skipCoolingPeriod,
            },
          },
        });
      } catch (err) {
        handleCommitError(err);
        throw err;
      }
    },
    [fireMutation, handleCommitError]
  );

  return {
    validateCommitOrder,
    ErrorDialog,
  };
};
